import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle } from 'lucide-react'
import { getDiagram } from '@/services/diagramService'
import { useInitialDataManager } from '@/hooks/useInitialDataManager'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useUiStateStore } from '@stores/uiStateStore'
import { AppLayout } from '@/components/Layout'

export const DiagramLoader: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { load } = useInitialDataManager()
  const isMobile = useIsMobile()
  const setEditorMode = useUiStateStore((state) => state.actions.setEditorMode)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadDiagram = async () => {
      if (!id) {
        setError('No diagram ID provided')
        setLoading(false)
        return
      }

      try {
        const { data, error: err } = await getDiagram(id)

        if (err || !data) {
          // If diagram not found, it's likely a new diagram with a fresh ID
          // Just show blank canvas instead of error
          console.log('📄 New diagram detected (ID not in database yet):', id)
          setLoading(false)
          return
        }

        // Load the diagram data into the model store
        load(data.data)

        // Clear localStorage autosave when loading existing diagram from cloud
        // This prevents the restore prompt from appearing
        localStorage.removeItem('flowi-autosave')
        localStorage.removeItem('flowi-autosave-timestamp')
        localStorage.removeItem('flowi-autosave-diagram-id')
        console.log('🧹 Cleared localStorage autosave (loaded existing diagram from cloud)')

        setLoading(false)
      } catch (err) {
        // On any error, assume it's a new diagram and show blank canvas
        console.log('📄 Treating as new diagram due to error:', err)
        setLoading(false)
      }
    }

    loadDiagram()
  }, [id, load])

  // Set editor mode based on device type
  useEffect(() => {
    if (isMobile) {
      console.log('📱 Mobile device detected - setting read-only mode')
      setEditorMode('EXPLORABLE_READONLY')
    } else {
      console.log('🖥️ Desktop device detected - setting editable mode')
      setEditorMode('EDITABLE')
    }
  }, [isMobile, setEditorMode])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-blue-600 dark:text-blue-400" />
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Loading diagram...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md">
          <AlertCircle className="mx-auto h-12 w-12 text-red-600 dark:text-red-400" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
            Failed to Load Diagram
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{error}</p>
          <div className="mt-6 flex gap-3 justify-center">
            <button
              onClick={() => navigate('/my-diagrams')}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              My Diagrams
            </button>
            <button
              onClick={() => navigate('/editor')}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              New Diagram
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Once loaded successfully, render the main editor
  return <AppLayout />
}
